import PurchasePlan from "../PurchasePlan";
import { useEffect, useState } from "react";
import { getSelectedPricingModelAsync } from "../../services/pricingmodels";
import {
  PricingModel,
  PricingModelItem,
  PricingModelType,
} from "../../model/pricingmodel";
import { getPaymentMethods } from "../../services/paymentMethods";
import { TalaeraError } from "../../model/general";
import { PaymentMethod } from "../../model/paymentMethod";
import AppDialog from "../AppDialog/AppDialog";
import "./styles.css";

export interface Props {
  onClose: () => void;
  onSuccess: () => void;
  reload: () => void;
  type: PricingModelType;
  backOptionsText?: string;
}

function usePurchaseInfo(type: PricingModelType) {
  const [loading, setLoading] = useState(true);
  const [pricingModel, setPricingModel] = useState<PricingModel>();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>();
  const [errors, setErrors] = useState<TalaeraError>();

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const action = async () => {
      const pricingModelRequest = getSelectedPricingModelAsync({ type });
      const paymentMethodsRequest = getPaymentMethods();
      try {
        const [pricingModelResp, paymentMethodsResp] = await Promise.all([
          pricingModelRequest,
          paymentMethodsRequest,
        ]);
        if (signal.aborted) {
          return;
        }
        setPricingModel(pricingModelResp);
        setPaymentMethods(paymentMethodsResp.list);
      } catch (e) {
        if (signal.aborted) {
          return;
        }
        setErrors(e as any);
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };
    action();
    return () => abortController.abort();
  }, [setLoading, setPaymentMethods, setPricingModel, type]);
  return { loading, pricingModel, paymentMethods, errors };
}

export default function PurchasePlanModal({
  onClose,
  onSuccess,
  reload,
  type,
  backOptionsText,
}: Props) {
  const { loading, pricingModel, paymentMethods, errors } = usePurchaseInfo(type);
  const [selectedPlan, setSelectedPlan] = useState<PricingModelItem | null>(null);

  useEffect(() => {
    if (pricingModel) {
      setSelectedPlan(pricingModel.items[0]);
    } else {
      setSelectedPlan(null);
    }
  }, [pricingModel]);

  return (
    <AppDialog
      closeOnOuterClick
      className="purchase-plan-modal-dialog"
      showDialog={true}
      onClose={onClose}
      mode="view"
      showSpinner={loading}
      errors={errors}
    >
      {!loading && selectedPlan && pricingModel && (
        <PurchasePlan
          pricingModel={pricingModel}
          paymentMethods={paymentMethods}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          onHide={onClose}
          onSuccess={onSuccess}
          reload={reload}
          backOptionsText={backOptionsText}
        />
      )}
    </AppDialog>
  );
}
